import React from 'react';
import PropTypes from 'prop-types';
import useStyles from '@front10/helpers/dist/UseStyles';
import { isSSR } from '@front10/utils';
import withCommonEMContainer from '../_commons/CommonEMContainer';

//-------------------------------------
// Basic components here
//-------------------------------------
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import DestinationList from '../../lists/DestinationList';
import Disclaimer from '../../others/Disclaimer';
import Skeleton from '../../others/Skeleton/SkeletonPopularDestinationsMobile';
import ContainerErrorAlert from '../../alerts/ContainerErrorAlert';
import NoFaresBehavior from '../../others/NoFaresBehavior';
import NoResults from '../../alerts/NoFeaturedPopularFaresAlert';
import Loading from '../../others/Loading';

//-------------------------------------
// EveryMundo integration
//-------------------------------------
import { getPopularDestinations } from '../../../_integrations/everymundo/destinations';
import { getAirports } from '../../../_integrations/everymundo/airports';

import { useFilters, OriginDestinationLogic } from '../_commons/filters';
import { useDestinationItemProps, trackSeeMore } from '../_commons/tracking';
import InlineFilters from '../../controls/InlineFilters/InlineFiltersMobile';
import { getResultsLength } from '../_commons/popularDestinations';
import HeaderContainer from '../../elements/HeaderContainer';

function PopularDestinationsContainerMobile(props) {
  const {
    prePopulation,
    integrationSettings,
    meta,
    pageContext,
    getAirports: getAirportsFn,
    getPopularDestinations: getFaresFn,
    showFilters,
    direction,
    initialPopularDestinations,
    fareItemOnClickAction,
    filters: { availableFilters, ...filters },
    showSkeletonScreen,
    destinationList,
    showDisclaimer,
    mainContentSettings,
    disclaimerContentSettings,
    disclaimer: disclaimerSettings,
    onClickSeeMore,
    skeletonSettings,
    noFaresBehavior,
    theme,
    colWidth,
    seeMoreBasicUrl,
    urlByDomain,
    domains,
    noResults,
    alert,
    forceShowCityName,
    loading,
    originDestinationLogic,
    enableSEO = true
  } = props;

  const {
    fares: { match, ...fares },
    dispatch,
    tracking,
    retry,
    ...filtersState
  } = useFilters({
    originDestinationLogic,
    filter: { prePopulation, integrationSettings, meta, pageContext, getAirports: getAirportsFn },
    initialData: initialPopularDestinations,
    minInitialResults: noFaresBehavior === 'hidden' ? 1 : null,
    getResultsLength,
    getFares: getFaresFn,
    faresTransformationLogic: 'popular-destinations',
    availableFilters: showFilters ? availableFilters : [],
    hideBudgetOnMultiCurrency: filters.hideBudgetOnMultiCurrency,
    showOrigin: filters.originDestinationSettings && filters.originDestinationSettings.showOrigin,
    showDestination:
      filters.originDestinationSettings && filters.originDestinationSettings.showDestination
  });

  const handleClickSeeMore = onClickSeeMore || trackSeeMore(dispatch);

  const destinationItemSettings = useDestinationItemProps(
    destinationList.destinationItem || {},
    dispatch,
    fareItemOnClickAction
  );

  const [className, style] = useStyles(props, { direction, theme });

  if (isSSR() && !Boolean(enableSEO)) {
    return null;
  }

  if (match('HIDDEN')) {
    return <NoFaresBehavior />;
  }

  function handleSeeMoreDeals() {
    tracking({ type: 'MORE_DEALS' });
    return true;
  }

  const header = (
    <>
      {match('LOADING') && <Loading {...loading} />}
      {showSkeletonScreen && match('SKELETON') && (
        <Skeleton {...skeletonSettings} position="absolute" width="100" />
      )}
      <HeaderContainer
        defaultTitleValue={'BEST FARES ON FLIGHTS'}
        handleSeeMoreDeals={handleSeeMoreDeals}
        {...props}
      />
    </>
  );

  const { siteEdition } = prePopulation;
  let seeMoreUrlByDomain = '';

  if (urlByDomain) {
    let sites = domains.siteEdition;
    let site = sites.filter((site) => site.name === siteEdition);
    if (site.length > 0) {
      seeMoreUrlByDomain = site[0].url;
    }
  }

  const popularDestinations = fares.data || {};

  const myStyle = Object.assign({ minHeight: '395px' }, style);
  const listColWidth = colWidth || popularDestinations.colWidth;

  return (
    <InlineFilters
      style={myStyle}
      className={`${className} ${PopularDestinationsContainerMobile.componentName}`}
      dataTestId="popularDestinationsContainer"
      header={header}
      {...filters}
      {...filtersState}
    >
      {match('NO_RESULTS') && <NoResults {...noResults} />}
      {match('COMPLETE') && (
        <Row marginTop="2" {...mainContentSettings}>
          <Col>
            <DestinationList
              {...destinationList}
              destinationItem={destinationItemSettings}
              onClickSeeMore={handleClickSeeMore}
              colWidth={listColWidth}
              oneColumn={popularDestinations.oneColumn}
              showCityName={forceShowCityName || popularDestinations.showCityName}
              columnNumbers={popularDestinations.columnNumbers}
              image={popularDestinations.image}
              label={popularDestinations.label}
              imageAlt={popularDestinations.label}
              labelSecondary={popularDestinations.labelSecondary}
              data={popularDestinations.data}
              siteEdition={siteEdition}
              seeMoreBasicUrl={
                urlByDomain && seeMoreUrlByDomain !== '' ? seeMoreUrlByDomain : seeMoreBasicUrl
              }
              showDefaultImage={popularDestinations.showDefaultImage}
              integrationSettings={integrationSettings}
            />
          </Col>
        </Row>
      )}
      {match('ERROR') && (
        <ContainerErrorAlert row={{ marginTop: '2' }} {...alert} tryAgain={retry} />
      )}
      {showDisclaimer && (
        <Row {...disclaimerContentSettings}>
          <Col>
            <Disclaimer labelKey="cmp-popular-destinations-disclaimer" {...disclaimerSettings} />
          </Col>
        </Row>
      )}
    </InlineFilters>
  );
}

PopularDestinationsContainerMobile.propTypes = {
  /**
   * headerContentSettings props
   */
  headerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * mainContentSettings props
   */
  mainContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * disclaimerContentSettings props
   */
  disclaimerContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Props of alerts
   */
  alert: PropTypes.objectOf(PropTypes.any),
  /**
   * To be used to set if the container is a controlled one
   */
  containerType: PropTypes.string,
  /**
   * Width of columns
   */
  colWidth: PropTypes.string,
  /**
   * DestinationList
   */
  destinationList: PropTypes.objectOf(PropTypes.any),
  /**
   * Disclaimer Component
   */
  disclaimer: PropTypes.objectOf(PropTypes.any),
  /**
   * Possible interactions will be Popup or Direct Deeplink
   */
  fareItemInteractionType: PropTypes.oneOf(['popup', 'deeplink']),
  /**
   * Action to be executed when clicking in fareItem
   */
  fareItemOnClickAction: PropTypes.func,
  /**
   * Filters settings
   */
  filters: PropTypes.objectOf(PropTypes.any),
  /**
   * Function called to search populars destinations
   */
  getPopularDestinations: PropTypes.func,
  /**
   * Function for request to obtain airports
   */
  getAirports: PropTypes.func,
  /**
   * Used to prepopulate the component
   */
  initialPopularDestinations: PropTypes.objectOf(PropTypes.any),
  /**
   * Loading Component
   */
  loading: PropTypes.objectOf(PropTypes.any),
  /**
   * Component Behavior when there are not results available
   */
  noFaresBehavior: PropTypes.oneOf(['hidden', 'showNoResults']),
  /**
   * NotResults Component
   */
  noResults: PropTypes.objectOf(PropTypes.any),
  /**
   * Handle to be called when seeMore is Clicked
   */
  onClickSeeMore: PropTypes.func,
  /**
   * Page Context
   */
  pageContext: PropTypes.shape({
    deviceCategory: PropTypes.string,
    siteEdition: PropTypes.string
  }),
  /**
   * Prepopulation of the component
   */
  prePopulation: PropTypes.objectOf(PropTypes.any),
  /**
   * Basic url for See More link
   */
  seeMoreBasicUrl: PropTypes.string,
  /**
   * Show disclaimer
   */
  showDisclaimer: PropTypes.bool,
  /**
   * Determine if the component will show the skeleton screen while
   * loading (Default value will be false)
   */
  showSkeletonScreen: PropTypes.bool,
  /**
   * Show title
   */
  showTitle: PropTypes.bool,
  /**
   * Show filters
   */
  showFilters: PropTypes.bool,
  /**
   * SkeletonSettings Component
   */
  skeletonSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Css style applied to the component
   */
  style: PropTypes.objectOf(PropTypes.any),
  /**
   * Title of the component
   */
  titleKey: PropTypes.string,
  /**
   * Title Component
   */
  titleSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Parameters tracking
   */
  tracking: PropTypes.objectOf(PropTypes.any),
  /**
   * Function to translate the component
   */
  translate: PropTypes.func,
  /**
   * Direction of the component rtl o ltr
   */
  direction: PropTypes.oneOf(['rtl', 'ltr']),
  /**
   * LocalizationData via moment to be used as a date translator
   * https://github.com/moment/moment/blob/develop/src/locale/es.js
   */
  localizationData: PropTypes.objectOf(PropTypes.any),
  /**
   * Theme object on the component
   */
  theme: PropTypes.objectOf(PropTypes.any),
  forceShowCityName: PropTypes.bool,
  /**
   * All integration configurations
   */
  integrationSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * All integration configurations
   */
  meta: PropTypes.objectOf(PropTypes.any),
  /**
   * Login to load origin and destination airports
   * same: Origin and Destination are pre-populated or not
   *      a single airport service call is done to fill out the Origin
   *      and Destinations filters (for performance purposes).
   * different: Origin and Destination are pre-populated or not
   *      2 airports service calls are done to fill out the Origin
   *      and Destinations filters.
   */
  originDestinationLogic: PropTypes.oneOf([
    OriginDestinationLogic.SAME,
    OriginDestinationLogic.DIFFERENT
  ]),
  /**
   * Show the SubTitle Component
   */
  showSubTitle: PropTypes.bool,
  /**
   * subHeaderContentSettings props
   */
  subHeaderContentSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * titleKey of the component
   */
  subTitleKey: PropTypes.string,
  /**
   * SubTitle Component
   */
  subTitleSettings: PropTypes.objectOf(PropTypes.any),
  /**
   * Url By Domain
   */
  urlByDomain: PropTypes.bool,
  /**
   * Domains
   */
  domains: PropTypes.objectOf(PropTypes.any)
};

PopularDestinationsContainerMobile.defaultProps = {
  disclaimerContentSettings: {},
  headerContentSettings: {},
  mainContentSettings: {},
  alert: {
    bgColor: 'secondary-4',
    textAlign: 'center',
    padding: '3',
    rounded: '@theme:card.rounded'
  },
  containerType: 'stateful',
  colWidth: null,
  destinationList: {},
  direction: 'ltr',
  disclaimer: {},
  fareItemInteractionType: 'popup',
  fareItemOnClickAction: null,
  getPopularDestinations,
  getAirports,
  initialPopularDestinations: null,
  loading: {},
  noFaresBehavior: 'hidden',
  noResults: {
    flavor: 'desktop'
  },
  showFilters: false,
  onClickSeeMore: null,
  pageContext: {
    deviceCategory: 'DESKTOP',
    siteEdition: 'en_HK'
  },
  prePopulation: {
    siteEdition: 'en-ca',
    languageCode: 'en',
    airlineIataCode: 'ac',
    flightType: 'ROUND_TRIP',
    travelClasses: ['ECONOMY'],
    priceFormat: {
      decimalSeparator: '.',
      thousandSeparator: '',
      decimalPlaces: 0
    },
    sorting: [{ popularity: 'DESC' }, { usdTotalPrice: 'ASC' }],
    datePattern: 'dd/MM/yyyy',
    outputCurrencies: ['CAD'],
    routesLimit: 3,
    faresPerRoute: 1,
    destinationsLimit: 6,
    dataExpirationWindow: '2d',
    outputFields: [
      'returnDate',
      'usdTotalPrice',
      'popularity',
      'originCity',
      'destinationCity',
      'destinationAirportImage',
      'destinationCityImage',
      'destinationStateImage',
      'destinationCountryImage',
      'destinationRegionImage',
      'farenetTravelClass',
      'travelClass',
      'flightDeltaDays',
      'totalPrice',
      'siteEdition',
      'flightType'
    ],
    budget: { maximum: null },
    departure: {
      start: 0,
      end: 30
    },
    markets: ['ca'],
    origins: [],
    destinations: [],
    currencyPricingSettings: [
      {
        currencyCode: 'CAD',
        priceFormat: {
          decimalSeparator: '.',
          thousandSeparator: ',',
          currencyInFront: true,
          currencyToDisplay: 'CA$'
        }
      }
    ]
  },
  seeMoreBasicUrl: !isSSR() ? window.location.hostname : null,
  showDisclaimer: true,
  showSkeletonScreen: true,
  showTitle: true,
  filters: {
    availableFilters: []
  },
  skeletonSettings: {
    style: { backgroundColor: 'white', paddingBottom: '1.5rem' }
  },
  style: {},
  titleKey: 'cmp-popular-destinations-title-no-country',
  titleSettings: {},
  tracking: {
    flexibleDates: false,
    passengerCount: 1,
    promoCode: 'EVERYMUNDO',
    eventType: 'fareListPopularDestination'
  },
  translate: () => {},
  localizationData: null,
  theme: null,
  forceShowCityName: false,
  integrationSettings: {},
  meta: null,
  originDestinationLogic: OriginDestinationLogic.SAME,
  showSubTitle: false,
  subHeaderContentSettings: {},
  subTitleKey: 'cmp-popular-destination-sub-title',
  subTitleSettings: {},
  urlByDomain: false,
  domains: {}
};
PopularDestinationsContainerMobile.componentName = 'PopularDestinationsContainerMobile';

// HOC with common functionality to all containers
export default withCommonEMContainer(PopularDestinationsContainerMobile);
